<template>
  <div
    class="min-h-screen overflow-x-hidden bg-bochas-bg px-3 py-6 sm:px-4 sm:py-8"
  >
    <div class="w-full min-w-0 max-w-7xl mx-auto">
      <!-- Header -->
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8"
      >
        <h1 class="text-2xl sm:text-3xl font-bold text-white">
          {{ isEdit ? "Editar torneo" : "Nuevo torneo" }}
        </h1>
        <router-link
          to="/admin/resultados"
          class="btn-bochas-trans text-sm self-start sm:self-auto"
        >
          ← Volver
        </router-link>
      </div>

      <form @submit.prevent="onSave" class="w-full min-w-0 space-y-6">
        <!-- Datos básicos -->
        <div class="card-fbochas min-w-0 p-4 sm:p-6 space-y-4">
          <h2 class="text-lg font-semibold text-white mb-2">
            Datos del torneo
          </h2>

          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2"
              >Nombre *</label
            >
            <input
              v-model="form.name"
              type="text"
              required
              class="w-full px-4 py-3 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo transition-colors"
              placeholder="Ej: Torneo por Parejas en Río Segundo"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2"
              >Descripción</label
            >
            <RichTextEditor
              v-model="form.description"
              placeholder="Organizado por Club Atlético Central. Equipos inscriptos: 34..."
            />
          </div>

          <div class="grid min-w-0 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">Provincia</label>
              <SelectField
                :model-value="form.provinceId"
                :options="provinceOptions"
                placeholder="Escribir o seleccionar provincia"
                allow-custom
                :maxlength="100"
                :disabled="institutionsLoading || !!institutionsError"
                @update:modelValue="onProvinceChange"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Club</label
              >
              <SelectField
                :model-value="organizerClubSelectValue"
                :options="clubOptions"
                placeholder="Escribir o seleccionar club"
                allow-custom
                :maxlength="150"
                @update:modelValue="onOrganizerClubChange"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Asociación</label
              >
              <SelectField
                :model-value="form.association"
                :options="associationOptions"
                placeholder="Escribir o seleccionar asociación"
                allow-custom
                :maxlength="150"
                @update:modelValue="onAssociationChange"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Localidad</label
              >
              <SelectField
                v-model="form.location"
                :options="localityOptions"
                placeholder="Escribir o seleccionar localidad"
                allow-custom
                :maxlength="150"
              />
            </div>
          </div>
          <div v-if="institutionsError" class="text-sm text-red-300" role="alert">
            {{ institutionsError }}
            <button type="button" class="underline ml-2" @click="loadInstituciones">Reintentar</button>
          </div>

          <!-- Fechas del torneo -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-300">
                Fechas del torneo
              </label>
            </div>
            <p class="text-xs text-gray-500 mb-3">
              Hacé click en cada día que se juegue. Se ordenan automáticamente y
              se arma el rango ("15 y 16 de agosto 2026") y el fin de semana al
              que pertenece.
            </p>
            <DateRangePickerField v-model="form.dates" />

            <!-- Preview del rango y fin de semana -->
            <div
              v-if="previewDateRange || previewWeekendLabel"
              class="mt-3 rounded-lg bg-white/5 border border-white/10 p-3 text-xs sm:text-sm space-y-1"
            >
              <p v-if="previewDateRange" class="text-white">
                <span class="text-gray-400">Rango:</span>
                {{ previewDateRange }}
              </p>
              <p v-if="previewWeekendLabel" class="text-white">
                <span class="text-gray-400">Fin de semana:</span>
                {{ previewWeekendLabel }}
              </p>
            </div>
            <p
              v-if="form.dates.length === 0"
              class="mt-2 text-xs text-gray-400"
            >
              Agregá al menos una fecha para continuar.
            </p>
          </div>

          <div class="flex items-center gap-6 pt-2">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="form.status"
                type="radio"
                value="draft"
                class="text-bochas-rojo focus:ring-bochas-rojo"
              />
              <span class="text-gray-300 text-sm">Borrador</span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="form.status"
                type="radio"
                value="published"
                class="text-bochas-rojo focus:ring-bochas-rojo"
              />
              <span class="text-gray-300 text-sm">Publicado</span>
            </label>
          </div>
        </div>

        <!-- Categorías -->
        <div class="card-fbochas min-w-0 p-4 sm:p-6 space-y-4">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold text-white">
              Categorías y podios
            </h2>
            <button
              type="button"
              @click="addCategory"
              class="btn-bochas-azul text-xs sm:text-sm"
            >
              + Agregar categoría
            </button>
          </div>

          <div
            v-if="form.categories.length === 0"
            class="text-center py-6 text-sm text-gray-400"
          >
            Agregá al menos una categoría con sus medallistas.
          </div>

          <div
            v-for="(cat, ci) in form.categories"
            :key="ci"
            class="min-w-0 bg-bochas-bg border border-gray-700 rounded-lg p-3 sm:p-4 space-y-3"
          >
            <div class="flex items-center justify-between gap-2">
              <div class="min-w-0 flex-1">
                <SelectField
                  v-model="cat.name"
                  :options="categoryOptionsFor(cat.name)"
                  placeholder="Seleccioná una categoría"
                />
              </div>
              <button
                type="button"
                @click="removeCategory(ci)"
                class="text-red-400 hover:text-red-300 text-sm px-2"
                title="Eliminar categoría"
              >
                ✕
              </button>
            </div>

            <details :open="!!(cat.city || cat.category_date)" class="space-y-2">
              <summary class="cursor-pointer text-sm text-gray-400 hover:text-white">
                Ciudad y fecha de la categoría (opcional)
              </summary>
              <div class="grid min-w-0 grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  v-model="cat.city"
                  type="text"
                  placeholder="Ciudad"
                  class="px-3 py-2 bg-bochas-bg border border-gray-700 rounded text-white text-sm placeholder-gray-500 focus:outline-none focus:border-bochas-rojo"
                />
                <DatePickerField
                  v-model="cat.category_date"
                  :name="`cat-date-${ci}`"
                  placeholder="Fecha de la categoría"
                  fixed-dropdown
                />
              </div>
            </details>

            <div>
              <label
                class="mb-2 block text-xs uppercase tracking-wide text-gray-400"
              >
                Nota de la categoría
              </label>
              <textarea
                v-model="cat.note"
                rows="2"
                placeholder="Ej: Observaciones, aclaraciones o información adicional de esta categoría"
                class="w-full resize-y rounded-lg border border-gray-700 bg-bochas-bg px-3 py-2 text-sm text-white placeholder-gray-500 transition-colors focus:border-bochas-rojo focus:outline-none"
              ></textarea>
            </div>

            <div class="space-y-2 pt-2">
              <p class="text-xs text-gray-400 uppercase tracking-wide">Podio</p>
              <div
                v-for="(podium, pi) in cat.podiums"
                :key="pi"
                class="flex items-center gap-2"
              >
                <div class="w-24 flex-shrink-0">
                  <SelectField
                    v-model="podium.position"
                    :options="podiumPositionOptions"
                    placeholder="Pos."
                  />
                </div>
                <input
                  v-model="podium.player_name"
                  type="text"
                  placeholder="Nombre del jugador/equipo y club"
                  aria-label="Nombre del jugador/equipo y club"
                  class="min-w-0 flex-1 px-3 py-2 bg-bochas-bg border border-gray-700 rounded text-white text-sm placeholder-gray-500 focus:outline-none focus:border-bochas-rojo"
                />
                <button
                  type="button"
                  @click="removePodium(ci, pi)"
                  class="text-red-400 hover:text-red-300 text-sm px-2"
                >
                  ✕
                </button>
              </div>
              <button
                type="button"
                @click="addPodium(ci)"
                class="text-bochas-rojo text-xs hover:underline"
              >
                + Agregar podio
              </button>
            </div>

            <div class="space-y-3 border-t border-white/10 pt-3">
              <div
                class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p class="text-xs text-gray-400 uppercase tracking-wide">
                    Fotos de esta categoría
                  </p>
                  <p class="text-xs text-gray-500">
                    Las imágenes quedan asociadas solo a esta categoría.
                  </p>
                </div>
                <label
                  class="inline-flex cursor-pointer items-center justify-center rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/5"
                >
                  Subir imágenes
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    class="hidden"
                    @change="onCategoryFileSelect(ci, $event)"
                  />
                </label>
              </div>

              <p v-if="!cat.id" class="text-xs text-gray-500">
                Las imágenes se subirán al guardar el torneo.
              </p>

              <div
                v-if="cat.images?.length"
                class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
              >
                <div
                  v-for="(img, idx) in cat.images"
                  :key="img.id"
                  class="group relative aspect-square overflow-hidden rounded-lg border-2"
                  :class="
                    img.is_cover ? 'border-bochas-rojo' : 'border-gray-700'
                  "
                >
                  <img
                    :src="img.url"
                    :alt="`Imagen ${idx + 1}`"
                    class="h-full w-full object-cover"
                  />
                  <div
                    v-if="img.is_cover"
                    class="absolute left-1 top-1 rounded bg-bochas-rojo px-2 py-0.5 text-[10px] font-bold uppercase text-white"
                  >
                    Portada
                  </div>
                  <div
                    class="absolute inset-0 flex items-center justify-center gap-1 bg-black/60 opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <button
                      v-if="!img.is_cover"
                      type="button"
                      @click.stop="setCategoryCover(ci, idx)"
                      class="rounded bg-bochas-rojo px-2 py-1 text-xs text-white"
                    >
                      Portada
                    </button>
                    <button
                      type="button"
                      @click.stop="removeCategoryImage(ci, idx)"
                      class="rounded bg-bochas-rojo px-2 py-1 text-xs text-white"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p v-if="uploadError" class="text-xs text-bochas-rojo">
            {{ uploadError }}
          </p>
        </div>

        <!-- Galería (solo si es edición) -->
        <div v-if="false" class="card-fbochas p-4 sm:p-6 space-y-4">
          <h2 class="text-lg font-semibold text-white">Galería de fotos</h2>

          <div
            @dragover.prevent="dragOver = true"
            @dragleave="dragOver = false"
            @drop.prevent="onDrop"
            :class="[
              'border-2 border-dashed rounded-lg p-4 sm:p-6 text-center transition-colors cursor-pointer',
              dragOver
                ? 'border-bochas-rojo bg-bochas-rojo/10'
                : 'border-gray-700 bg-white/5 hover:border-gray-500',
            ]"
            @click="$refs.fileInput.click()"
          >
            <input
              ref="fileInput"
              type="file"
              multiple
              accept="image/*"
              class="hidden"
              @change="onFileSelect"
            />
            <p class="text-sm text-gray-300">
              <span class="font-semibold text-bochas-rojo"
                >Tocá para subir</span
              >
              <span class="hidden sm:inline"> o arrastrá imágenes</span>
            </p>
            <p class="text-xs text-gray-500 mt-1">
              PNG, JPG o WEBP · Máx. 10 por vez
            </p>
          </div>

          <div
            v-if="imágenes.length > 0"
            class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
          >
            <div
              v-for="(img, idx) in imágenes"
              :key="img.id"
              class="relative group rounded-lg overflow-hidden border-2 aspect-square"
              :class="img.is_cover ? 'border-bochas-rojo' : 'border-gray-700'"
            >
              <img
                :src="img.url"
                :alt="`Imagen ${idx + 1}`"
                class="w-full h-full object-cover"
              />
              <div
                v-if="img.is_cover"
                class="absolute top-1 left-1 bg-bochas-rojo text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase"
              >
                Portada
              </div>
              <div
                class="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1"
              >
                <button
                  v-if="!img.is_cover"
                  type="button"
                  @click.stop="setCover(idx)"
                  class="px-2 py-1 bg-bochas-rojo text-white text-xs rounded"
                >
                  Portada
                </button>
                <button
                  type="button"
                  @click.stop="removeImage(idx)"
                  class="px-2 py-1 bg-bochas-rojo text-white text-xs rounded"
                >
                  ✕
                </button>
              </div>
              <div
                v-if="img.uploading"
                class="absolute inset-0 bg-black/70 flex items-center justify-center"
              >
                <div
                  class="w-6 h-6 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin"
                ></div>
              </div>
            </div>
          </div>

          <p v-if="uploadError" class="text-xs text-bochas-rojo">
            {{ uploadError }}
          </p>
        </div>

        <!-- Error -->
        <div
          v-if="error"
          class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 text-sm rounded-lg px-4 py-3"
        >
          {{ error }}
        </div>

        <!-- Submit -->
        <div class="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            :disabled="saving"
            class="btn-bochas-rojo"
            :class="{ 'opacity-60 cursor-not-allowed': saving }"
          >
            <span v-if="!saving">{{
              isEdit ? "Guardar cambios" : "Crear torneo"
            }}</span>
            <span v-else>Guardando...</span>
          </button>
          <router-link
            to="/admin/resultados"
            class="btn-bochas-trans text-center"
          >
            Cancelar
          </router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  crearTorneo,
  editarTorneo,
  obtenerTorneo,
  listarCategoriasCatalogo,
  subirImagenesTorneo,
  marcarPortada,
  eliminarImagenTorneo,
  subirImagenesCategoria,
  marcarPortadaCategoria,
  eliminarImagenCategoria,
  obtenerInstitucionesResultados,
} from "@/services/tournaments/tournamentsService";
import { listarAsociacionesAdmin } from "@/services/asociaciones/asociacionesService";
import { listarClubesAdmin } from "@/services/clubes/clubesService";
import { obtenerProvinciasAdmin } from "@/services/admin/adminModulesService";
import { toDateInputValue } from "@/utils/date";
import DatePickerField from "@/components/Common/DatePickerField.vue";
import DateRangePickerField from "@/components/Common/DateRangePickerField.vue";
import SelectField from "@/components/Common/SelectField.vue";
import RichTextEditor from "@/components/Common/RichTextEditor.vue";
import { useConfirm } from "@/composables/useConfirm";

const route = useRoute();
const router = useRouter();
const { confirm } = useConfirm();

const createdTournamentId = ref(null);
const isEdit = computed(() => !!route.params.id || !!createdTournamentId.value);
const torneoId = computed(() => createdTournamentId.value || parseInt(route.params.id, 10));

const form = reactive({
  name: "",
  description: "",
  location: "",
  association: "",
  organizer: "",
  provinceId: "",
  status: "published",
  dates: [],
  categories: [],
});

const saving = ref(false);
const error = ref("");
const uploadError = ref("");
const dragOver = ref(false);
const uploading = ref(false);

const imágenes = ref([]);
const asociaciones = ref([]);
const clubes = ref([]);
const provincias = ref([]);
const institutions = ref([]);
const institutionsLoading = ref(true);
const institutionsError = ref("");
const selectedOrganizerClubId = ref("");

function normalizeInstitution(value) {
  return String(value || "").trim().replace(/\s+/g, " ").normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

const provinceOptions = computed(() => [
  { value: "", label: "Sin provincia" },
  ...provincias.value.map((item) => ({ value: item.id, label: item.name })),
]);
const selectedProvince = computed(() => provincias.value.find((item) =>
  item.id === form.provinceId || normalizeInstitution(item.name) === normalizeInstitution(form.provinceId)
));
const provinceInstitutions = computed(() => institutions.value.filter((item) =>
  String(item.provinceId || "") === String(selectedProvince.value?.id || form.provinceId || "")
));

function institutionOptions(values) {
  const unique = new Map();
  for (const value of values) {
    if (value?.trim()) unique.set(normalizeInstitution(value), value.trim());
  }
  return [...unique.values()].sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }))
    .map((value) => ({ value, label: value }));
}

const provinceAssociations = computed(() => asociaciones.value.filter((item) =>
  !form.provinceId || String(item.provinceId || "") === (selectedProvince.value?.id || form.provinceId)
));
const provinceClubs = computed(() => clubes.value.filter((club) =>
  !form.provinceId || String(club.provinceId || "") === (selectedProvince.value?.id || form.provinceId) ||
    provinceAssociations.value.some((item) => Number(item.id) === Number(club.asociacionId))
));
const associationOptions = computed(() => institutionOptions([
  ...provinceInstitutions.value.map((item) => item.association),
  ...provinceAssociations.value.map((item) => item.name),
]));
const localityOptions = computed(() => institutionOptions([
  ...provinceInstitutions.value.map((item) => item.location),
  ...provinceClubs.value.map((item) => item.locality),
  ...provinceAssociations.value.map((item) => item.locality),
]));
const organizerClubSelectValue = computed(() => selectedOrganizerClubId.value || form.organizer);
const clubOptions = computed(() => [
  ...provinceClubs.value
    .filter((club) => !form.association || asociaciones.value.some((association) =>
      normalizeInstitution(association.name) === normalizeInstitution(form.association) &&
      Number(association.id) === Number(club.asociacionId)))
    .map((club) => ({
      value: `club:${club.id}`,
      label: [club.name, asociaciones.value.find((item) => Number(item.id) === Number(club.asociacionId))?.name, club.locality]
        .filter(Boolean).join(" · "),
      inputLabel: club.name,
    })),
  ...provinceInstitutions.value
    .filter((item) => !provinceClubs.value.some((club) => {
      const association = asociaciones.value.find((entry) => Number(entry.id) === Number(club.asociacionId));
      return normalizeInstitution(club.name) === normalizeInstitution(item.organizer) &&
        normalizeInstitution(club.locality) === normalizeInstitution(item.location) &&
        String(club.provinceId || association?.provinceId || "") === String(item.provinceId || "") &&
        (!item.association || !association?.name ||
          normalizeInstitution(association.name) === normalizeInstitution(item.association));
    }))
    .filter((item) => item.organizer && (!form.association || !item.association ||
      normalizeInstitution(item.association) === normalizeInstitution(form.association)))
    .map((item) => ({
      value: `institution:${item.id}`,
      label: [item.organizer, item.association, item.location].filter(Boolean).join(" · "),
      inputLabel: item.organizer,
    })),
]);

const fileInput = ref(null);
const podiumPositions = [1, 2, 3, 4, 5, 6, 7, 8];
const podiumPositionOptions = podiumPositions.map((position) => ({
  value: position,
  label: `${position}°`,
}));
const dbCategories = ref([]);

const baseCategoryOptions = computed(() =>
  dbCategories.value.map((cat) => ({
    value: cat.name,
    label: cat.name,
  })),
);

function categoryOptionsFor(value) {
  const options = baseCategoryOptions.value;
  if (!value || options.some((option) => option.value === value)) {
    return options;
  }
  return [{ value, label: value }, ...options];
}

async function loadCategorias() {
  try {
    const data = await listarCategoriasCatalogo();
    const items = Array.isArray(data) ? data : data.items || [];
    dbCategories.value = items;
  } catch (_) {
    dbCategories.value = [];
    error.value = "No se pudieron cargar las categorías.";
  }
}

async function loadAsociaciones() {
  try {
    const data = await listarAsociacionesAdmin();
    asociaciones.value = data.items || [];
  } catch (_) {
    asociaciones.value = [];
  }
}

async function loadClubes() {
  try {
    const data = await listarClubesAdmin();
    clubes.value = data.items || [];
  } catch (_) {
    clubes.value = [];
  }
}

async function loadInstituciones() {
  institutionsLoading.value = true;
  institutionsError.value = "";
  try {
    const [provinceData, institutionData] = await Promise.all([
      obtenerProvinciasAdmin(), obtenerInstitucionesResultados(),
    ]);
    provincias.value = provinceData;
    institutions.value = institutionData.items || [];
  } catch (err) {
    institutionsError.value = err.response?.data?.message || "No se pudieron cargar las provincias y sugerencias.";
  } finally {
    institutionsLoading.value = false;
  }
}

function onProvinceChange(value) {
  if (form.provinceId === value) return;
  form.provinceId = value || "";
  selectedOrganizerClubId.value = "";
  if (!value) {
    form.organizer = "";
    form.association = "";
    form.location = "";
  }
}

function onAssociationChange(value) {
  form.association = value || "";
  if (normalizeInstitution(selectedProvince.value?.name) !== "cordoba") return;
  const selectedAssociation = provinceAssociations.value.find((item) =>
    normalizeInstitution(item.name) === normalizeInstitution(value));
  if (selectedAssociation?.locality && !form.location) form.location = selectedAssociation.locality;
  const locations = [...new Set(provinceInstitutions.value
    .filter((item) => normalizeInstitution(item.association) === normalizeInstitution(value))
    .map((item) => item.location).filter(Boolean))];
  if (value && !form.location && locations.length === 1) form.location = locations[0];
}

function onOrganizerClubChange(value) {
  selectedOrganizerClubId.value = "";
  const selectedClub = clubes.value.find((club) => `club:${club.id}` === value);
  const selectedInstitution = provinceInstitutions.value.find((item) => `institution:${item.id}` === value);
  if (selectedInstitution) {
    selectedOrganizerClubId.value = value;
    form.organizer = selectedInstitution.organizer || "";
    form.association = selectedInstitution.association || "";
    if (normalizeInstitution(selectedProvince.value?.name) === "cordoba") {
      form.location = selectedInstitution.location || "";
    }
  } else if (selectedClub) {
    selectedOrganizerClubId.value = value;
    form.organizer = selectedClub.name || "";
    if (normalizeInstitution(selectedProvince.value?.name) === "cordoba") {
      form.location = selectedClub.locality || "";
    }
    form.association = asociaciones.value.find((item) => Number(item.id) === Number(selectedClub.asociacionId))?.name || "";
  } else {
    form.organizer = value || "";
  }
}

function addCategory() {
  form.categories.push({
    name: "",
    city: "",
    category_date: "",
    note: "",
    podiums: [],
    images: [],
  });
}

// === Preview en vivo del rango y fin de semana ===
const PREVIEW_MONTHS_ES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function previewFormatDateRange(dateStrs) {
  if (!Array.isArray(dateStrs) || dateStrs.length === 0) return "";
  const parsed = dateStrs
    .map((s) => {
      const [y, m, d] = String(s)
        .split("T")[0]
        .split("-")
        .map((n) => parseInt(n, 10));
      if (!y || !m || !d) return null;
      return { y, m, d };
    })
    .filter(Boolean);
  if (parsed.length === 0) return "";
  const first = parsed[0];
  const sameMonth = parsed.every((p) => p.m === first.m && p.y === first.y);
  if (!sameMonth) {
    return parsed
      .map((p) => `${p.d} de ${PREVIEW_MONTHS_ES[p.m - 1]} ${p.y}`)
      .join(", ");
  }
  const days = parsed.map((p) => p.d);
  let daysPart;
  if (days.length === 1) daysPart = `${days[0]}`;
  else if (days.length === 2) daysPart = `${days[0]} y ${days[1]}`;
  else {
    const head = days.slice(0, -1).join(", ");
    const last = days[days.length - 1];
    daysPart = `${head} y ${last}`;
  }
  return `${daysPart} de ${PREVIEW_MONTHS_ES[first.m - 1]} ${first.y}`;
}

function computePreviewWeekendLabel(dateStrs) {
  if (!Array.isArray(dateStrs) || dateStrs.length === 0) return "";
  const first = dateStrs[0];
  const [y, m, d] = String(first)
    .split("T")[0]
    .split("-")
    .map((n) => parseInt(n, 10));
  if (!y || !m || !d) return "";
  const date = new Date(y, m - 1, d);
  const dow = date.getDay();
  let saturday;
  if (dow === 6) saturday = date;
  else if (dow === 0) saturday = new Date(y, m - 1, d - 1);
  else return `Fin de semana del ${d} de ${PREVIEW_MONTHS_ES[m - 1]} ${y}`;
  const firstOfMonth = new Date(y, m - 1, 1);
  const firstDow = firstOfMonth.getDay();
  const offsetToFirstSat = (6 - firstDow + 7) % 7;
  const firstSat = new Date(y, m - 1, 1 + offsetToFirstSat);
  const diffDays = Math.round(
    (saturday.getTime() - firstSat.getTime()) / (1000 * 60 * 60 * 24),
  );
  const nth = Math.floor(diffDays / 7) + 1;
  const ordinal =
    nth === 1
      ? "Primer"
      : nth === 2
      ? "Segundo"
      : nth === 3
      ? "Tercer"
      : nth === 4
      ? "Cuarto"
      : "Último";
  return `${ordinal} fin de semana de ${PREVIEW_MONTHS_ES[m - 1]} ${y}`;
}

const sortedDates = computed(() =>
  (form.dates || []).filter(Boolean).slice().sort(),
);

const previewDateRange = computed(() =>
  previewFormatDateRange(sortedDates.value),
);
const previewWeekendLabel = computed(() =>
  computePreviewWeekendLabel(sortedDates.value),
);

async function removeCategory(ci) {
  const confirmed = await confirm({
    title: "Eliminar categoría",
    message: "¿Eliminar está categoría?",
  });
  if (!confirmed) return;
  form.categories[ci].images?.forEach((img) => {
    if (img.file) URL.revokeObjectURL(img.url);
  });
  form.categories.splice(ci, 1);
}

function addPodium(ci) {
  form.categories[ci].podiums.push({
    position: form.categories[ci].podiums.length + 1,
    player_name: "",
    club: "",
  });
}

function removePodium(ci, pi) {
  form.categories[ci].podiums.splice(pi, 1);
}

async function loadTorneo() {
  if (!isEdit.value) return;
  try {
    const data = await obtenerTorneo(torneoId.value);
    Object.assign(form, {
      name: data.name || "",
      description: data.description || "",
      location: data.location || "",
      association: data.association || "",
      organizer: data.organizer || "",
      provinceId: data.provinceId || "",
      status: data.status || "published",
      dates: ((data.dates || []).length
        ? data.dates
        : [{ event_date: data.event_date, display_order: 0 }]
      )
        .slice()
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map((d) => toDateInputValue(d.event_date))
        .filter(Boolean),
      categories: (data.categories || []).map((c) => ({
        id: c.id,
        name: c.name || "",
        city: c.city || "",
        category_date: toDateInputValue(c.category_date),
        note: c.note || "",
        images: c.images || [],
        podiums: (c.podiums || []).map((p) => ({
          position: p.position,
          player_name: [p.player_name, p.club].filter(Boolean).join(" — "),
          club: "",
        })),
      })),
    });
    imágenes.value = (data.images || []).map((img) => ({
      id: img.id,
      url: img.url,
      is_cover: !!img.is_cover,
      uploading: false,
    }));
  } catch (err) {
    error.value = "No se pudo cargar el torneo.";
  }
}

async function onSave() {
  if (saving.value) return;
  saving.value = true;
  error.value = "";
  try {
    const cleanDates = form.dates.slice().filter(Boolean).sort();

    if (cleanDates.length === 0) {
      error.value = "Agregá al menos una fecha al torneo.";
      saving.value = false;
      return;
    }

    const categoriesToSave = form.categories.filter((c) => c.name.trim());
    if (form.categories.some((c) => !c.name.trim() && c.images?.some((img) => img.file))) {
      error.value = "Seleccioná una categoría para las imágenes cargadas.";
      return;
    }
    const payload = {
      name: form.name,
      description: form.description || null,
      // event_date = primera fecha del rango. El backend lo usa como
      // ordenamiento principal; el header del card usa dates[].
      event_date: cleanDates[0],
      location: form.location.trim() || null,
      association: form.association.trim() || null,
      organizer: form.organizer.trim() || null,
      provinceId: selectedProvince.value?.id || null,
      provinceName: selectedProvince.value ? null : form.provinceId.trim() || null,
      status: form.status,
      dates: cleanDates.map((event_date) => ({ event_date })),
      categories: form.categories
        .filter((c) => c.name.trim())
        .map((c) => ({
          id: c.id,
          name: c.name,
          city: c.city || null,
          category_date: c.category_date || null,
          note: c.note || null,
          podiums: c.podiums
            .filter((p) => p.player_name.trim())
            .map((p) => ({
              position: p.position,
              player_name: p.player_name,
              club: p.club || null,
            })),
        })),
    };

    let saved;
    if (isEdit.value) {
      saved = await editarTorneo(torneoId.value, payload);
    } else {
      saved = await crearTorneo(payload);
      createdTournamentId.value = saved.id;
    }
    if (saved.provinceId && saved.provinceId !== form.provinceId) {
      form.provinceId = saved.provinceId;
      await loadInstituciones();
    }
    const existingIds = new Set(categoriesToSave.map((cat) => cat.id).filter(Boolean));
    const newCategories = (saved.categories || []).filter((cat) => !existingIds.has(cat.id)).sort((a, b) => a.id - b.id);
    for (const cat of categoriesToSave) {
      if (!cat.id) cat.id = newCategories.shift()?.id;
    }
    for (const cat of categoriesToSave) {
      for (const img of [...(cat.images || [])]) {
        if (!img.file) continue;
        if (!cat.id) throw new Error("No se pudo identificar la categoría para subir las imágenes.");
        const result = await subirImagenesCategoria(torneoId.value, cat.id, [img.file]);
        const uploaded = result.items?.[0];
        if (!uploaded) throw new Error("No se pudo confirmar la imagen subida.");
        URL.revokeObjectURL(img.url);
        const cover = img.is_cover;
        Object.assign(img, uploaded, { file: null, is_cover: cover });
      }
      const cover = cat.images?.find((img) => img.is_cover);
      if (cover) await marcarPortadaCategoria(torneoId.value, cat.id, cover.id);
    }
    router.push("/admin/resultados");
  } catch (err) {
    error.value =
      err.response?.data?.message || err.message || "No se pudo guardar el torneo.";
  } finally {
    saving.value = false;
  }
}

async function uploadFiles(files) {
  if (!isEdit.value || !files || files.length === 0) return;
  uploading.value = true;
  uploadError.value = "";

  try {
    const res = await subirImagenesTorneo(torneoId.value, files);
    // refrescamos la galería
    await loadTorneo();
  } catch (err) {
    uploadError.value =
      err.response?.data?.message || "Error al subir las imágenes.";
  } finally {
    uploading.value = false;
  }
}

async function uploadCategoryFiles(ci, files) {
  const cat = form.categories[ci];
  if (!cat || !files || files.length === 0) return;
  const selectedFiles = Array.from(files);
  if (!cat.id) {
    cat.images ||= [];
    for (const file of selectedFiles) {
      const url = URL.createObjectURL(file);
      cat.images.push({ id: url, url, file, is_cover: cat.images.length === 0 });
    }
    return;
  }
  uploadError.value = "";
  try {
    const result = await subirImagenesCategoria(torneoId.value, cat.id, selectedFiles);
    cat.images.push(...(result.items || []));
  } catch (err) {
    uploadError.value =
      err.response?.data?.message || "Error al subir las imágenes.";
  }
}

function onCategoryFileSelect(ci, e) {
  uploadCategoryFiles(ci, e.target.files);
  e.target.value = "";
}

async function setCategoryCover(ci, idx) {
  const cat = form.categories[ci];
  const img = cat?.images?.[idx];
  if (!img) return;
  if (img.file) {
    cat.images.forEach((item) => { item.is_cover = item === img; });
    return;
  }
  try {
    await marcarPortadaCategoria(torneoId.value, cat.id, img.id);
    cat.images.forEach((item) => { item.is_cover = item === img; });
  } catch (err) {
    uploadError.value = "No se pudo marcar cómo portada.";
  }
}

async function removeCategoryImage(ci, idx) {
  const cat = form.categories[ci];
  const img = cat?.images?.[idx];
  if (!img) return;
  if (img.file) {
    URL.revokeObjectURL(img.url);
    cat.images.splice(idx, 1);
    if (img.is_cover && cat.images.length) cat.images[0].is_cover = true;
    return;
  }
  const confirmed = await confirm({
    title: "Eliminar imagen",
    message: "¿Eliminar está imagen?",
  });
  if (!confirmed) return;
  try {
    await eliminarImagenCategoria(torneoId.value, cat.id, img.id);
    cat.images.splice(idx, 1);
  } catch (err) {
    uploadError.value = "No se pudo eliminar la imagen.";
  }
}

function onFileSelect(e) {
  uploadFiles(e.target.files);
  e.target.value = "";
}

function onDrop(e) {
  dragOver.value = false;
  uploadFiles(e.dataTransfer.files);
}

async function setCover(idx) {
  const img = imágenes.value[idx];
  try {
    await marcarPortada(torneoId.value, img.id);
    await loadTorneo();
  } catch (err) {
    uploadError.value = "No se pudo marcar cómo portada.";
  }
}

async function removeImage(idx) {
  const img = imágenes.value[idx];
  const confirmed = await confirm({
    title: "Eliminar imagen",
    message: "¿Eliminar está imagen?",
  });
  if (!confirmed) return;
  try {
    await eliminarImagenTorneo(torneoId.value, img.id);
    await loadTorneo();
  } catch (err) {
    uploadError.value = "No se pudo eliminar la imagen.";
  }
}

onBeforeUnmount(() => {
  form.categories.forEach((cat) => cat.images?.forEach((img) => {
    if (img.file) URL.revokeObjectURL(img.url);
  }));
});

onMounted(async () => {
  await Promise.all([
    loadInstituciones(),
    loadAsociaciones(),
    loadClubes(),
    loadCategorias(),
    loadTorneo(),
  ]);
  if (!isEdit.value) {
    form.provinceId = provincias.value.find((item) => normalizeInstitution(item.name) === "cordoba")?.id || "";
  }
});
</script>
